import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  deleteDoc,
  runTransaction,
  serverTimestamp,
  addDoc,
  collection,
} from "firebase/firestore";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyAEsrHK3k5gCS7ZnXXlvsoqz0DZFKFwjTk",
  authDomain: "pushnotification-26fdb.firebaseapp.com",
  projectId: "pushnotification-26fdb",
  storageBucket: "pushnotification-26fdb.firebasestorage.app",
  messagingSenderId: "687591429394",
  appId: "1:687591429394:web:2efc71c01eefc60035cfcb",
  measurementId: "G-V2B83EBP07",
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const messaging = getMessaging(app);

// ─── FCM ───────────────────────────────────────────────────────────────────
export const getFcmToken = async () => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
    return await getToken(messaging, {
      vapidKey:
        "BAVE9PdQhTayMEIwvmmDkQ9ZSKDNcAW7nmkcVopCbEEvM4jX370bcwjNiwSfjKJ4mPDNiXpXSr_0m1Eoxk1bwaI",
    });
  } catch (err) {
    console.error("FCM Token Error", err);
    return null;
  }
};

export const onForegroundMessage = (cb) => onMessage(messaging, cb);

// ─── CALL STATUS ENUM ──────────────────────────────────────────────────────
export const CALL_STATUS = {
  RINGING: 1,
  IN_CALL: 2,
  REJECTED: -1,
  ENDED: -2,
};



export const createCallNotification = async ({
  callerId,
  callerName,
  callerRole,
  receiverId,
  roomId,
}) => {
  const callRef = doc(db, "calls", roomId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(callRef);
    if (snap.exists()) {
      const existingStatus = snap.data()?.incomingCall?.status;
      // Only block if a call for this appointment is genuinely still live.
      // A leftover REJECTED/ENDED doc from a prior attempt must not block
      // a brand-new call for the same appointment.
      if (
        existingStatus === CALL_STATUS.RINGING ||
        existingStatus === CALL_STATUS.IN_CALL
      ) {
        return;
      }
    }
    tx.set(callRef, {
      incomingCall: {
        callerName,
        callerId,
        role: callerRole,
        roomId,
        receiverId,
        status: CALL_STATUS.RINGING,
        createdAt: serverTimestamp(),
      },
    });
  });
};

export const notifyDoctor = async (patientId, roomId, callerName, doctorId) =>
  createCallNotification({
    callerId: patientId,
    callerName: callerName,
    callerRole: "patient",
    receiverId: doctorId,
    roomId,
  });


export const deleteCallRecord = async (roomId, currentUserId) => {
  if (!roomId || !currentUserId) return;
  try {
    const callRef = doc(db, "calls", roomId);
    const snap = await getDoc(callRef);
    if (!snap.exists()) return; // already gone — nothing to do

    const { callerId, receiverId } = snap.data()?.incomingCall || {};

    if (currentUserId !== callerId && currentUserId !== receiverId) {
      // Not a participant in this call — do not delete.
      console.warn("deleteCallRecord: user not a participant — skipped.");
      return;
    }
    await deleteDoc(callRef);
  } catch (err) {
    console.error("deleteCallRecord error:", err);
  }
};

/*** Notify doctor that patient uploaded a diet image. */
export const notifyDietImageUpload = async ({
  id,
  patientId,
  patientName,
  doctorId,        // payload.userId
  consultationId,  // payload.consultationId
  chartTitle,      // payload.chartTitle
  meal,            // patientDietPlanDetails item .meal
  mealTime,        // patientDietPlanDetails item .mealTime  { hour, minute }
  dayTitle,        // patientDietPlanDetails item .dayTitle
  compliance,
}) => {
  try {
    await addDoc(collection(db, "dietImageUploads"), {
      id,
      patientId,
      patientName,
      doctorId,
      consultationId,
      chartTitle,
      meal,
      mealTime,       // { hour, minute, second, nano }
      dayTitle,
      seen: false,
      createdAt: serverTimestamp(),
      compliance
    });
  } catch (err) {
    console.error("notifyDietImageUpload error:", err);
  }
};

// ─── MEDICAL RECORD SHARE ──────────────────────────────────────────────────
/**
 * Notify the doctor that the patient shared medical records for an appointment.
 * Writes to the "medicalrecordShare" collection (NOT "calls").
 *
 * @param {object} params
 * @param {string} params.senderId    - patientId
 * @param {string} params.receiverId  - doctorId (appointmentData.userId)
 * @param {string} params.apptId      - appointmentId
 */
export const notifyMedicalRecordShare = async ({
  senderId,
  receiverId,
  apptId,
}) => {
  if (!senderId || !receiverId || !apptId) {
    console.warn("notifyMedicalRecordShare: missing required field", {
      senderId,
      receiverId,
      apptId,
    });
    return;
  }

  try {
    await addDoc(collection(db, "medicalrecordShare"), {
      apptId,
      senderId,
      receiverId,
      handled: false,        // the doctor side flips this to true once seen
      timestamp: Date.now(), // number, matches your existing docs
    });
  } catch (err) {
    console.error("notifyMedicalRecordShare error:", err);
  }
};
